import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Stack,
  Alert,
  InputAdornment,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import { 
  Email,
  ArrowBack,
  CheckCircle,
  Send
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { EasyCheckButton } from '../../common/Controls';
import { easyCheckAuthService } from '../../../services/auth/authService';
import { themeUtils } from '../../../theme';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}20`,
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}30`,
    },
    '& fieldset': {
      borderColor: themeUtils.colors.blue['blue-100'],
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: themeUtils.colors.blue['blue-300'],
    },
    '&.Mui-focused fieldset': {
      borderColor: themeUtils.colors.blue['blue-600'],
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    backgroundColor: 'transparent',
    '&.Mui-focused': {
      color: themeUtils.colors.blue['blue-600'],
    },
    '&.MuiInputLabel-shrink': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '0 8px',
      marginLeft: '-4px',
    },
  },
}));

const ForgotPasswordForm = ({ onBackToLogin }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues
  } = useForm({
    defaultValues: {
      email: ''
    }
  });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');
      
      const result = await easyCheckAuthService.forgotPassword(data.email);
      
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.message || 'An error occurred while sending the reset email.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card sx={{ borderRadius: 3, maxWidth: 400, mx: 'auto' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <CheckCircle sx={{ 
            fontSize: 64, 
            color: 'success.main', 
            mb: 2 
          }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Check Your Email
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If an account with <strong>{getValues('email')}</strong> exists, we've sent a password reset link to your email.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The link will expire in 1 hour. If you don't receive the email, check your spam folder.
          </Typography>
          <EasyCheckButton
            buttonType="auth"
            variant="outlined"
            fullWidth
            onClick={onBackToLogin}
            sx={{ mt: 2 }}
          >
            Back to Login
          </EasyCheckButton>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, maxWidth: 400, mx: 'auto' }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={onBackToLogin}
            sx={{ 
              mr: 1, 
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Forgot Password
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your email address and we'll send you a link to reset your password.
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Controller
              name="email"
              control={control}
              rules={{ 
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address'
                }
              }}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  label="Email Address"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: themeUtils.colors.blue['blue-600'], opacity: 0.7 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <EasyCheckButton
              buttonType="auth"
              type="submit"
              variant="contained"
              fullWidth
              loading={loading}
              size="large"
              startIcon={<Send />}
              sx={{
                background: themeUtils.gradients.primary,
                '&:hover': {
                  background: themeUtils.gradients.primaryDark,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 35px ${themeUtils.colors.blue['blue-600']}40`,
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
                padding: '12px 24px',
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '6px',
                boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}30`,
              }}
            >
              {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
            </EasyCheckButton>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ForgotPasswordForm; 